import { PhoneNumberUtil } from 'google-libphonenumber';
import { useState } from 'react';
import useCreateParkingService from '../../../hooks/parking-services/useCreateParkingService.ts';
import useParkingService from '../../../hooks/parking-services/useParkingService.ts';
import useUpdateParkingServiceOperationHours from '../../../hooks/parking-services/useUpdateParkingServiceOperationHours.ts';
import useUpdateParkingServiceStoreIds from '../../../hooks/parking-services/useUpdateParkingServiceStoreIds.ts';
import MutateParkingServiceBasicInformationForm, {
  MutateParkingServiceBasicInformationFormValues,
} from '../../ui/places/MutateParkingServiceBasicInformationForm.tsx';
import {
  CreateParkingServiceRequest,
  UpdateParkingServiceOperationHoursRequest,
} from '../../../api/requests/parking-service-request.ts';
import {
  ExistenceStatus,
  PricingSource,
} from '../../../models/PlaceService.ts';
import { notifyError } from '../../../utils/NotifyError.tsx';
import { MechanicalSpaces } from '../../../models/ParkingService.ts';
import {
  Button,
  Group,
  Loader,
  Modal,
  Stack,
  Stepper,
  Text,
  TextInput,
} from '@mantine/core';
import MutateParkingServiceOperationHoursForm, {
  MutateParkingServiceOperationHoursFormValues,
} from '../../ui/places/MutateParkingServiceOperationHoursForm.tsx';
import MutateParkingServiceImagesForm from '../../ui/places/MutateParkingServiceImagesForm.tsx';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ErrorResponse } from '../../../api/responses/error-response.ts';
import { JUPITER_BASE_URL, parseStoreUrl } from '../../../utils/storeUrl.ts';

const phoneUtil = PhoneNumberUtil.getInstance();

interface AddParkingServiceModalProps {
  opened: boolean;
  onClose: () => void;
  placeId: string;
  placeName: string;
  onSuccess?: (serviceId: string) => void;
}

const AddParkingServiceModal = ({
  opened,
  onClose,
  placeId,
  placeName,
  onSuccess,
}: AddParkingServiceModalProps) => {
  const [active, setActive] = useState(0);

  const [parkingServiceId, setParkingServiceId] = useState<string | null>(null);

  const [storeIdUrl, setStoreIdUrl] = useState('');
  const [storeIdError, setStoreIdError] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const { mutateAsync: createParkingService } = useCreateParkingService();

  const { data: parkingService, isLoading: isParkingServiceLoading } =
    useParkingService(parkingServiceId);

  const { mutateAsync: updateParkingServiceOperationHours } =
    useUpdateParkingServiceOperationHours();

  const {
    mutateAsync: updateParkingServiceStoreIds,
    isPending: isStoreIdPending,
  } = useUpdateParkingServiceStoreIds();

  // 新增停車基本資訊 mutation
  const {
    mutate: handleBasicInformationSubmit,
    isPending: isBasicInformationPending,
  } = useMutation<
    { id: string },
    ErrorResponse,
    MutateParkingServiceBasicInformationFormValues
  >({
    mutationFn: async (formValues) => {
      const parsedPhoneNumber =
        formValues.phoneNumberCountryCode !== '' &&
        formValues.phoneNumberValue !== ''
          ? phoneUtil.parse(
              `${formValues.phoneNumberCountryCode}${formValues.phoneNumberValue}`,
            )
          : null;

      const createParkingServiceRequest: CreateParkingServiceRequest = {
        placeId: placeId,
        name: formValues.name.trim(),
        brandId: formValues.brandId ?? '',
        serviceStatus: formValues.serviceStatus,
        pricingSource: 'UNKNOWN' as PricingSource,
        operationHours: {
          weeklySchedule: [
            {
              dayOfWeek: 'MONDAY',
              isOpen: 'OPEN',
              timeSlots: [{ startTime: '00:00:00', endTime: '23:59:00' }],
            },
            {
              dayOfWeek: 'TUESDAY',
              isOpen: 'OPEN',
              timeSlots: [{ startTime: '00:00:00', endTime: '23:59:00' }],
            },
            {
              dayOfWeek: 'WEDNESDAY',
              isOpen: 'OPEN',
              timeSlots: [{ startTime: '00:00:00', endTime: '23:59:00' }],
            },
            {
              dayOfWeek: 'THURSDAY',
              isOpen: 'OPEN',
              timeSlots: [{ startTime: '00:00:00', endTime: '23:59:00' }],
            },
            {
              dayOfWeek: 'FRIDAY',
              isOpen: 'OPEN',
              timeSlots: [{ startTime: '00:00:00', endTime: '23:59:00' }],
            },
            {
              dayOfWeek: 'SATURDAY',
              isOpen: 'OPEN',
              timeSlots: [{ startTime: '00:00:00', endTime: '23:59:00' }],
            },
            {
              dayOfWeek: 'SUNDAY',
              isOpen: 'OPEN',
              timeSlots: [{ startTime: '00:00:00', endTime: '23:59:00' }],
            },
          ],
          holidaySchedule: [],
          temporaryClosures: [],
          timezone: 'UTC+8',
        },
        phoneNumber: {
          countryCode: formValues.phoneNumberCountryCode ?? '',
          number: parsedPhoneNumber
            ? phoneUtil.getNationalSignificantNumber(parsedPhoneNumber)
            : '',
        },
        website: formValues.website,
        notes: formValues.notes,
        tags: formValues.tags,
        paymentMethods: formValues.paymentMethods,
        availableCarSpaces: null,
        totalCarSpaces: null,
        availableMotoSpaces: null,
        totalMotoSpaces: null,
        totalHeavyMotoSpaces: null,
        totalDisabledParkingSpaces: null,
        totalMaternityParkingSpaces: null,
        parkingRate: {
          regularRules: [],
          specificRules: [],
        },
        heightLimit: null,
        hasRestroom: 'UNKNOWN' as ExistenceStatus,
        hasElevator: 'UNKNOWN' as ExistenceStatus,
        hasParkingBooth: 'UNKNOWN' as ExistenceStatus,
        buildingTypes: [],
        mechanicalSpaces: 'UNKNOWN' as MechanicalSpaces,
        parkingLotTypes: [],
        floorLevels: [],
      };

      return await createParkingService(createParkingServiceRequest);
    },
    onSuccess: (data) => {
      setParkingServiceId(data.id);
      setActive(1);
    },
    onError: (error) => {
      notifyError('新增停車服務失敗', error.message);
    },
  });

  // 綁定營運商店（可選填，跳過則直接進到下一步）
  const handleStoreIdSubmit = async () => {
    if (!storeIdUrl.trim()) {
      setActive(2);
      return;
    }

    let entry: ReturnType<typeof parseStoreUrl>;
    try {
      entry = parseStoreUrl(storeIdUrl, 'PARKING');
    } catch (error) {
      setStoreIdError(error instanceof Error ? error.message : '網址格式錯誤');
      return;
    }

    try {
      await updateParkingServiceStoreIds({
        serviceId: parkingServiceId,
        storeIds: [entry],
      });
      await queryClient.invalidateQueries({
        queryKey: ['parkingServices', parkingServiceId],
        exact: true,
      });
      setActive(2);
    } catch (error) {
      notifyError(
        '綁定營運商店失敗',
        error instanceof Error ? error.message : String(error),
      );
    }
  };

  // 更新停車營業時間 mutation
  const {
    mutate: handleOperationHoursSubmit,
    isPending: isOperationHoursPending,
  } = useMutation<
    void,
    ErrorResponse,
    MutateParkingServiceOperationHoursFormValues
  >({
    mutationFn: async (formValues) => {
      const updateParkingServiceOperationHoursRequest: UpdateParkingServiceOperationHoursRequest =
        {
          serviceId: parkingServiceId,
          operationHours: formValues.operationHours,
        };

      await updateParkingServiceOperationHours(
        updateParkingServiceOperationHoursRequest,
      );
    },
    onSuccess: () => {
      setActive(3);
    },
    onError: (error) => {
      notifyError('新增停車服務失敗', error.message);
    },
  });

  const handleImageSubmit = async () => {
    onSuccess?.(parkingServiceId);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="新增停車服務"
      centered
      closeOnClickOutside={false}
      size={659}
      styles={{
        title: { fontWeight: 700, fontSize: 16 },
        header: { paddingBottom: 24 },
      }}
    >
      <Stepper
        size={'sm'}
        active={active}
        allowNextStepsSelect={false}
        styles={{
          root: {
            justifyItems: 'center',
          },
          steps: {
            width: 600,
            paddingBottom: 24,
          },
          content: {
            width: 627,
            paddingTop: 0,
          },
        }}
      >
        {/* Step 1: 停車服務基本資料 */}
        <Stepper.Step label={'基本資訊'}>
          <MutateParkingServiceBasicInformationForm
            initialValues={{
              name: placeName,
              serviceStatus: null,
              brandId: '',
              phoneNumberCountryCode: '',
              phoneNumberValue: '',
              website: '',
              notes: '',
              tags: [],
              paymentMethods: [],
            }}
            onSubmit={handleBasicInformationSubmit}
            isSubmitting={isBasicInformationPending}
            onCancel={onClose}
            isUpdating={false}
          />
        </Stepper.Step>

        {/* Step 2: 綁定營運商店（可選） */}
        <Stepper.Step label={'營運商店'}>
          <Stack gap={'md'}>
            <Text size={'sm'} c={'dimmed'}>
              如果這個服務已經在 autoplus
              有對應的商店，可以貼上網址進行綁定，下一步的營業時間與停車費率將會直接帶入
              autoplus 現有資料。也可以先跳過，之後再從服務詳情頁面綁定。
            </Text>
            <TextInput
              label={'營運商店'}
              placeholder={`${JUPITER_BASE_URL}/parkinglots/{id}/...`}
              value={storeIdUrl}
              onChange={(e) => {
                setStoreIdUrl(e.currentTarget.value);
                setStoreIdError(null);
              }}
              error={storeIdError}
            />
            <Group justify={'flex-end'}>
              <Button variant={'outline'} onClick={onClose}>
                取消
              </Button>
              <Button
                onClick={handleStoreIdSubmit}
                loading={isStoreIdPending}
                loaderProps={{ type: 'oval', size: 'xs' }}
              >
                下一步
              </Button>
            </Group>
          </Stack>
        </Stepper.Step>

        {/* Step 3: 停車服務營業時間 */}
        <Stepper.Step label={'營業時間'}>
          {isParkingServiceLoading ? (
            <Loader />
          ) : (
            <MutateParkingServiceOperationHoursForm
              initialValues={{
                operationHours: parkingService?.operationHours,
              }}
              onSubmit={handleOperationHoursSubmit}
              isSubmitting={isOperationHoursPending}
              onCancel={onClose}
              isUpdating={false}
            />
          )}
        </Stepper.Step>

        {/* Step 4. 停車服務圖片 */}
        <Stepper.Step label={'上傳圖片'}>
          {isParkingServiceLoading ? (
            <Loader />
          ) : (
            <MutateParkingServiceImagesForm
              initialValues={{
                parkingServiceId: parkingServiceId,
              }}
              onSubmit={handleImageSubmit}
              onCancel={onClose}
              isUpdating={false}
            />
          )}
        </Stepper.Step>
      </Stepper>
    </Modal>
  );
};

export { AddParkingServiceModal };
